import apiClient, { getWithDedupe } from './client'

import type {
  PaginatedResponse,
  ITTicket,
  ITTicketInput,
  ITTicketListItem,
  ITTicketMetrics,
  ITTicketActivity,
  ITTicketAttachment,
  ITTicketTimeEntry,
  Usuario
} from '@/types/gia'

export interface ITTicketFilters {
  page?: number
  page_size?: number
  folder?: 'all' | 'unassigned' | 'mine' | 'assigned' | 'pending' | 'closed'
  q?: string
  centro?: number | number[]
  estado?: string | string[]
  prioridad?: string | string[]
  cola?: string | string[]
  asignado_a?: number | 'none'
  timer_enabled?: boolean
  mine?: boolean
  watching?: boolean
}

const normalizeFilters = (filters: ITTicketFilters) => {
  const params: Record<string, any> = { ...filters }

  ;(['centro', 'estado', 'prioridad', 'cola'] as const).forEach(key => {
    const value = filters[key]

    if (Array.isArray(value)) {
      params[key] = value.length ? value.join(',') : undefined
    }
  })

  if (filters.mine !== undefined) {
    params.mine = filters.mine ? '1' : undefined
  }

  if (filters.watching !== undefined) {
    params.watching = filters.watching ? '1' : undefined
  }

  if (filters.timer_enabled !== undefined) {
    params.timer_enabled = filters.timer_enabled ? '1' : '0'
  }

  return params
}

const itApi = {
  list: async (filters: ITTicketFilters = {}): Promise<PaginatedResponse<ITTicketListItem>> => {
    return getWithDedupe<PaginatedResponse<ITTicketListItem>>('/it/tickets/', {
      params: normalizeFilters(filters)
    })
  },

  get: async (id: number): Promise<ITTicket> => {
    const response = await apiClient.get<ITTicket>(`/it/tickets/${id}/`)

    
return response.data
  },

  create: async (data: ITTicketInput): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>('/it/tickets/', data)

    
return response.data
  },

  update: async (id: number, data: Partial<ITTicketInput>): Promise<ITTicket> => {
    const response = await apiClient.patch<ITTicket>(`/it/tickets/${id}/`, data)

    
return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/it/tickets/${id}/`)
  },

  metrics: async (filters: Omit<ITTicketFilters, 'page' | 'page_size'> = {}): Promise<ITTicketMetrics> => {
    return getWithDedupe<ITTicketMetrics>('/it/tickets/metrics/', {
      params: normalizeFilters(filters)
    })
  },

  assignees: async (params?: { centro?: number | null }): Promise<Usuario[]> => {
    return getWithDedupe<Usuario[]>('/it/tickets/assignees/', {
      params: { centro: params?.centro ?? undefined }
    })
  },

  addComment: async (id: number, body: string): Promise<ITTicketActivity> => {
    const response = await apiClient.post<ITTicketActivity>(`/it/tickets/${id}/add_comment/`, { body })

    
return response.data
  },

  setWatchers: async (id: number, watchers_ids: number[]): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/set_watchers/`, { watchers_ids })

    
return response.data
  },

  setCollaborators: async (id: number, colaboradores_ids: number[]): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/set_collaborators/`, { colaboradores_ids })

    
return response.data
  },

  assignToMe: async (id: number): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/assign_to_me/`)

    
return response.data
  },

  assign: async (id: number, asignado_a: number | null): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/assign/`, { asignado_a })

    
return response.data
  },

  unassign: async (id: number): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/unassign/`)

    
return response.data
  },

  markPending: async (id: number): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/mark_pending/`)

    
return response.data
  },

  close: async (id: number): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/close/`)

    
return response.data
  },

  reopen: async (id: number): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/reopen/`)

    
return response.data
  },

  attachments: async (id: number): Promise<ITTicketAttachment[]> => {
    const response = await apiClient.get<ITTicketAttachment[]>(`/it/tickets/${id}/attachments/`)

    
return response.data
  },

  uploadAttachment: async (
    id: number,
    payload: { file: File; tipo?: 'photo' | 'video' | 'audio' | 'document'; nombre?: string }
  ): Promise<ITTicketAttachment> => {
    const fd = new FormData()

    fd.append('file', payload.file)
    if (payload.tipo) fd.append('tipo', payload.tipo)
    if (payload.nombre) fd.append('nombre', payload.nombre)

    const response = await apiClient.post<ITTicketAttachment>(`/it/tickets/${id}/upload_attachment/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    
return response.data
  },

  transition: async (
    id: number,
    payload: {
      estado?: string
      prioridad?: string
      asignado_a?: number | null
      evaluacion_tecnica?: string
      rechazo_motivo?: string
      timer_enabled?: boolean
    }
  ): Promise<ITTicket> => {
    const response = await apiClient.post<ITTicket>(`/it/tickets/${id}/transition/`, payload)

    
return response.data
  },

  startTimer: async (id: number): Promise<ITTicketTimeEntry> => {
    const response = await apiClient.post<ITTicketTimeEntry>(`/it/tickets/${id}/start_timer/`)

    
return response.data
  },

  pauseTimer: async (id: number): Promise<ITTicketTimeEntry> => {
    const response = await apiClient.post<ITTicketTimeEntry>(`/it/tickets/${id}/pause_timer/`)

    
return response.data
  }
}

export default itApi
